import { AddCircle, Folder, Login } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import React, { cloneElement, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ButtonContentProps } from '../../@types/Components';
import { EModal } from '../../@types/Enums';
import useResponsiveSizes, { IResponsiveSizes } from '../../hooks/useResponsiveSizes';
import { useModalContext } from '../../provider/ModalProvider';
import { useSceneData } from '../../provider/SceneDataProvider';
import PanelButton from '../common/PanelButton';

import { IconRoom2, IconRoom3, IconRoom4 } from '../../assets/icons';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import ImsIcon from '../common/ImsIcon';

const styles = {
  box: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    fontFamily: 'Roboto',
    width: 'fit-content',
  },
  headerTypography: (sizes: IResponsiveSizes) => ({
    fontWeight: '500',
    color: 'black',
    margin: {
      xs: '.2rem .25rem .3rem',
      sm: '.25rem .25rem .35rem',
      md: '.3rem .25rem .4rem',
      lg: '.4rem .25rem .5rem',
      xl: '.5rem .25rem .6rem',
    },
    padding: sizes.isHeightOver800 ? '.2rem' : '.05rem',
    fontFamily: 'Roboto',
    fontSize: {
      xs: '2.2rem',
      sm: '2.3rem',
      md: '2.5rem',
      lg: '2.7rem',
      xl: '2.9rem',
    },
    lineWrap: 'nowrap',
    wordBreak: 'keep-all',
    overflow: 'visible',
    whiteSpace: 'nowrap',
  }),
  gridContainer: {
    height: '100%',
    width: '100%',
    alignSelf: 'center',
    justifySelf: 'center',
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '65%',
  },
  icon: (sizes: IResponsiveSizes) => ({
    color: '#87d3ff',
    padding: sizes.iconPadding,
    backgroundColor: 'white',
    margin: sizes.iconMargin,
    borderRadius: '1rem',
    width: `${sizes.fontSize} !important`,
    height: `${sizes.fontSize} !important`,
    maxWidth: `${sizes.fontSize} !important`,
    maxHeight: `${sizes.fontSize} !important`,
  }),
  typography: (sizes: IResponsiveSizes) => ({
    fontWeight: '600',
    m: '.25rem 0 .45rem',
    fontSize: `calc(${sizes.fontSize}*.27)`,
  }),
};

const CreateNewModal = () => {
  const sizes = useResponsiveSizes();
  const { setSceneNeedsClearing } = useSceneData();
  const { setActiveModal, setIsModalShown } = useModalContext();

  const { panoMeasurements, setMeasurementModalOpen, handleImportMeasurements, setBuilding, setShowFetchingInfoModal } = useBuilding();
  const { addCameraPositionSprite, addHotspot } = useAnnotations();
  const { setRoomCompleted } = useDrawing();

  useEffect(() => {
    setActiveModal(EModal.CREATE_NEW);
    setIsModalShown(true);
  }, [setActiveModal, setIsModalShown]);

  const handleStartEmptyClick = () => {
    setSceneNeedsClearing(true);
    setIsModalShown(false);
  };

  const buttonContent: ButtonContentProps[] = [
    {
      id: 0,
      icon: <AddCircle key="icon-0" />,
      labelId: 'CreateNewModal.startEmpty',
      onClick: () => handleStartEmptyClick(),
    },
    {
      id: 1,
      icon: <Folder key="icon-1" />,
      labelId: 'CreateNewModal.loadPlan',
      onClick: () => console.log('btn1 clicked'),
      disabled: true,
    },
    {
      id: 2,
      icons: [
        <ImsIcon Icon={IconRoom4} key="room4" style={{ ...styles.icon(sizes), maxHeight: sizes.fontSize, maxWidth: sizes.fontSize }} />,
        <ImsIcon Icon={IconRoom3} key="room3" style={{ ...styles.icon(sizes), maxHeight: sizes.fontSize, maxWidth: sizes.fontSize }} />,
        <ImsIcon Icon={IconRoom2} key="room2" style={{ ...styles.icon(sizes), maxHeight: sizes.fontSize, maxWidth: sizes.fontSize }} />,
      ],
      labelId: 'CreateNewModal.selectForm',
      onClick: () => setActiveModal(EModal.PREDEFINED_ROOMS),
    },
    {
      id: 3,
      icon: <Login key="icon-3" />,
      labelId: 'CreateNewModal.importMeasurement',
      onClick: async () => {
        // Only run if there are measurements.
        if (panoMeasurements.length === 1) {
          // Auto-import using the hook’s handler for one measurement
          await handleImportMeasurements(panoMeasurements);
        } else if (panoMeasurements.length > 1) {
          // The hook will open the modal for multiple measurements
          setMeasurementModalOpen(true);
        }
        setIsModalShown(false);
      },
      disabled: !panoMeasurements || panoMeasurements.length === 0,
    },
  ];

  const renderButton = (button: ButtonContentProps): React.JSX.Element => (
    <Grid key={`modalElement-${button.id}`} item xs={6} className="createNewModalElement" sx={styles.gridItem}>
      <PanelButton
        id={`panelBtn${button.id}`}
        innerContent={
          <Box component="div" sx={styles.iconBox}>
            {button.icons
              ? button.icons.map((icon, index) =>
                  cloneElement(icon, {
                    sx: styles.icon(sizes),
                    key: `icon-${button.id}-${index}`,
                  }),
                )
              : button.icon
                ? cloneElement(button.icon, { sx: styles.icon(sizes) })
                : null}
          </Box>
        }
        onClick={button.onClick}
        onHover={button.onHover}
        disabled={button.disabled}
      />
      <Typography noWrap sx={styles.typography(sizes)}>
        <FormattedMessage id={button.labelId} />
      </Typography>
    </Grid>
  );

  return (
    <Box component="div" sx={styles.box}>
      <Typography variant="h3" sx={styles.headerTypography(sizes)}>
        <FormattedMessage id="CreateNewModal.header" />
      </Typography>
      <Grid container sx={styles.gridContainer}>
        {buttonContent.map(renderButton)}
      </Grid>
    </Box>
  );
};

export default CreateNewModal;
